import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, demoTrial } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");
  const destination = redirectParam && redirectParam.startsWith("/app") ? redirectParam : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [trialing, setTrialing] = useState(false);

  const startTrial = async () => {
    setError("");
    setTrialing(true);
    try {
      const u = await demoTrial();
      toast.success(t("auth.welcome").replace("{name}", u.name));
      navigate(destination);
    } catch (err) {
      setError(t("auth.tryDemoError"));
    } finally {
      setTrialing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(t("auth.welcome").replace("{name}", u.name));
      navigate(destination);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("admin@progere.ca");
    setPassword("Progere2026!");
  };

  return (
    <AuthShell
      title={t("auth.loginTitle")}
      subtitle={t("auth.loginSubtitle")}
      footer={
        <>
          {t("auth.noAccount")}{" "}
          <Link to="/register" className="text-[#EAB308] hover:underline font-medium">
            {t("auth.createAccount")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.email")}</Label>
          <Input
            data-testid="login-email"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.emailPh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-white/80">{t("auth.password")}</Label>
            <Link to="/mot-de-passe-oublie" data-testid="forgot-password-link" className="text-xs text-white/50 hover:text-[#EAB308] transition-colors">
              {t("auth.forgotLink")}
            </Link>
          </div>
          <Input
            data-testid="login-password"
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>

        {error && (
          <div data-testid="login-error" className="rounded-md border border-[#dc143c]/40 bg-[#dc143c]/10 px-4 py-2.5 text-sm text-red-300">
            {error}
          </div>
        )}

        <Button
          data-testid="login-submit"
          type="submit" disabled={loading}
          className="w-full h-11 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90 transition-opacity"
        >
          {loading ? t("auth.loginSubmitting") : t("auth.loginSubmit")}
        </Button>

        <button
          type="button" onClick={fillDemo} data-testid="login-demo-fill"
          className="w-full text-center text-sm text-white/50 hover:text-[#EAB308] transition-colors"
        >
          {t("auth.useDemo")}
        </button>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
        </div>

        <Button
          type="button" onClick={startTrial} disabled={trialing}
          data-testid="login-demo-trial"
          className="w-full h-11 bg-transparent border border-[#EAB308]/50 text-[#EAB308] font-semibold rounded-md hover:bg-[#EAB308]/10 transition-colors"
        >
          {trialing ? t("auth.tryDemoLoading") : t("auth.tryDemo")}
        </Button>
      </form>
    </AuthShell>
  );
}
